import type { QueryClient } from '@tanstack/react-query';
import type { useGT } from 'gt-react';
import type { GetSuggestionDetailResponse } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import type { OverrideOptionInput } from '@erp/rpc';
import { applyBulkFulfillmentOverrides } from '../utils/bulkOverrideApply';
import type { BulkOverrideForm } from '../utils/bulkFulfillmentOverride';
import { vendorBuyOverrideInput } from '../utils/vendorCandidateRows';
import type { OverrideSectionVendor } from './overrideSectionTypes';

type Gt = ReturnType<typeof useGT>;

export type OverrideSectionSubmitArgs = {
  queryClient: QueryClient;
  gt: Gt;
  suggestion: GetSuggestionDetailResponse;
  bulkLineCount: number;
  linesForBulk: readonly AssignmentLine[];
  form: BulkOverrideForm;
  overrideMutateAsync: (input: OverrideOptionInput) => Promise<unknown>;
  isBuy: boolean;
  isTransfer: boolean;
  effectiveAction: BulkOverrideForm['action'];
  effectiveVendorId: string;
  effectiveSourceId: string;
  qty: number;
  vendor: OverrideSectionVendor | undefined;
};

export type OverrideSubmitResult = { kind: 'close' } | { kind: 'noop' };

/**
 * Save the buyer's override. A transfer is saved like any other plan: the line
 * stays in its vendor cart and its transfer order is created when the cart is
 * released, once the line is approved (GEN-3023).
 */
export async function runOverrideSectionSubmit(
  args: OverrideSectionSubmitArgs,
): Promise<OverrideSubmitResult> {
  const {
    queryClient,
    gt,
    suggestion,
    bulkLineCount,
    linesForBulk,
    form,
    overrideMutateAsync,
    isBuy,
    isTransfer,
    effectiveAction,
    effectiveVendorId,
    effectiveSourceId,
    qty,
    vendor,
  } = args;

  if (bulkLineCount > 1) {
    const { applied } = await applyBulkFulfillmentOverrides(
      queryClient,
      gt,
      linesForBulk,
      bulkLineCount,
      form,
      (input) => overrideMutateAsync(input),
    );
    return applied > 0 ? { kind: 'close' } : { kind: 'noop' };
  }

  // A plain buy is routed like the supplier picker's: a vendor that only
  // delivers to a hub buys there and transfers on to the branch (GEN-3306).
  if (effectiveAction === 'buy' && vendor !== undefined) {
    await overrideMutateAsync(vendorBuyOverrideInput(suggestion, vendor, qty));
    return { kind: 'close' };
  }

  await overrideMutateAsync({
    suggestionId: suggestion.suggestionId,
    action: effectiveAction,
    supplierId: isBuy ? effectiveVendorId : null,
    shipToWarehouseId: isBuy ? suggestion.destinationWarehouseId : null,
    buyQuantity: isBuy ? qty : null,
    unitCost: isBuy ? (vendor?.unitPrice ?? null) : null,
    sourceWarehouseId: isTransfer ? effectiveSourceId : null,
    transferQuantity: isTransfer ? qty : null,
    setChosen: true,
  });
  return { kind: 'close' };
}
